import { InputBaseProps } from '@mui/material'
import FormControl, { FormControlProps } from '@mui/material/FormControl'
import InputLabel, { InputLabelProps } from '@mui/material/InputLabel'
import Select from '@mui/material/Select'
import I18n from 'i18n-js'
import { useMemo, useRef } from 'react'

import MenuItemWithCheckbox from '@src/lib/MenuItemWithCheckbox'

interface Option {
  label: string
  value: string
}

export interface SearchableFilterProps {
  options: Option[]
  slotProps?: {
    formControl?: FormControlProps
    inputLabel?: InputLabelProps
  }
  onChange: (value?: string | string[]) => void
  label: string
  value?: string | string[]
  multiple?: boolean
  showAll?: boolean
  size?: InputBaseProps['size']
  key?: string
}

const SearchableFilter: React.FC<
  React.PropsWithChildren<SearchableFilterProps>
> = ({
  options,
  label,
  multiple,
  onChange,
  value,
  showAll,
  size,
  slotProps,
}) => {
  const inputLabel = useRef<HTMLLabelElement>(null)
  const allValue = '0'
  const availableOptions = showAll
    ? [{ label: I18n.t('filters.all'), value: allValue }, ...options]
    : options

  const valueToRender = useMemo(() => {
    if (multiple && !value?.length && showAll) {
      return [allValue]
    } else if (!value && showAll) {
      return allValue
    }
    return value
  }, [multiple, value, showAll, allValue])

  return (
    <FormControl sx={{ width: '100%' }} {...slotProps?.formControl}>
      <InputLabel id={label} ref={inputLabel} {...slotProps?.inputLabel}>
        {label}
      </InputLabel>
      <Select
        size={size}
        label={label}
        labelId={label}
        sx={{
          backgroundColor: (theme) => theme.palette.background.paper,
        }}
        multiple={multiple}
        onChange={(event) => {
          let valueToEmit = event.target.value
          if (multiple) {
            if (valueToEmit[valueToEmit.length - 1] === allValue) {
              valueToEmit = []
            } else {
              valueToEmit = Array.from(valueToEmit).filter(
                (value: string) => value !== allValue,
              )
            }
          } else {
            if (valueToEmit === allValue) {
              valueToEmit = ''
            }
          }
          onChange(valueToEmit)
        }}
        value={valueToRender}
        fullWidth
      >
        {availableOptions.map(({ label, value }) => (
          <MenuItemWithCheckbox key={`menu-item-${value}`} value={`${value}`}>
            {label}
          </MenuItemWithCheckbox>
        ))}
      </Select>
    </FormControl>
  )
}

export default SearchableFilter
